import {
  Check,
  ChefHat,
  ChevronDown,
  NotebookPen,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";
import { useSettings } from "@/hooks/useSettings";
import {
  replaceFoodClarifications,
  replaceRecipes,
  upsertRecipe,
} from "@/lib/repos/settings";
import {
  type FoodClarification,
  MEAL_METRICS,
  type Meal,
  type MealLogConfig,
  type MealLogDayData,
  type Recipe,
} from "@/lib/schema";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";
import { orderedConditions } from "./conditions";
import { FoodClarificationsModal } from "./FoodClarificationsModal";
import { MealFormModal } from "./MealFormModal";
import { MetricCellDensity } from "./MetricCellDensity";
import { conditionLabel, formatAmount, METRIC_LABELS_SHORT } from "./metrics";
import { RecipesModal } from "./RecipesModal";
import {
  asMealLogDayData,
  dayTotals,
  isConditionMet,
  maxScoreDay,
  scoreDay,
} from "./scoring";

type Props = WidgetDayViewProps<MealLogConfig>;

const PREVIEW_MEALS: Meal[] = [
  {
    id: "preview-1",
    name: "Havregrøt med bær",
    kcal: 350,
    protein: 12,
    carbs: 55,
    fat: 8,
    fiber: 6,
  },
  {
    id: "preview-2",
    name: "Kyllingsalat",
    kcal: 450,
    protein: 35,
    carbs: 20,
    fat: 22,
    fiber: 5,
  },
];

export function DayView({
  config,
  data,
  dayKey,
  onChangeDay,
  readOnly = false,
}: Props) {
  const realData = asMealLogDayData(data);
  const showPreview = readOnly && realData.meals.length === 0;
  const meals = showPreview ? PREVIEW_MEALS : realData.meals;
  const totals = dayTotals(meals);
  // Betingelsesrekkefølge (docs/adr/0021) — every list below reads from this,
  // never from the stored array, so a snapshot sealed before the order existed
  // still renders in it.
  const conditions = orderedConditions(config.conditions);
  const hasConditions = conditions.length > 0;
  const score = scoreDay(config, realData, dayKey);
  const maxDay = maxScoreDay(config);

  // Ferdiglogget (CONTEXT.md): "Ferdig for i dag" is meal-log's route to it.
  // Day finished stays its own concept — it also gates scoring and strips
  // ingredient detail (docs/adr/0001), neither of which Ferdiglogget implies.
  const loggingCompleted = !readOnly && realData.finished === true;

  const { open, toggle } = useAutoCollapse(dayKey, loggingCompleted);
  const collapsible = !readOnly;
  const collapsed = collapsible && !open;

  const [modal, setModal] = useState<{ open: boolean; meal: Meal | null }>({
    open: false,
    meal: null,
  });
  const [clarificationsOpen, setClarificationsOpen] = useState(false);
  const [recipesOpen, setRecipesOpen] = useState(false);

  // Matpresiseringer and Oppskrifter are live per-user data (docs/adr/0004,
  // docs/adr/0005) — read from current settings, never from the sealed
  // week snapshot.
  const { settings } = useSettings();
  const foodClarifications = settings.foodClarifications ?? [];
  const recipes = settings.recipes ?? [];

  // Both await before closing, matching handleSettingsSave, so the modal
  // disappearing *means* the write landed rather than merely coinciding with
  // it. Fire-and-forget left a window where reloading dropped the write
  // outright — narrow enough that reverting this alone does not resurrect
  // the e2e flake, but it is the difference between a guarantee and a
  // timing accident.
  const saveClarifications = async (next: FoodClarification[]) => {
    await replaceFoodClarifications(next);
    setClarificationsOpen(false);
  };

  const saveRecipes = async (next: Recipe[]) => {
    await replaceRecipes(next);
    setRecipesOpen(false);
  };

  const saveRecipe = (recipe: Recipe) => {
    void upsertRecipe(recipe);
  };

  const write = (patch: Partial<MealLogDayData>) => {
    onChangeDay({ ...realData, ...patch } satisfies MealLogDayData);
  };

  const saveMeal = (meal: Meal) => {
    const exists = realData.meals.some((m) => m.id === meal.id);
    write({
      meals: exists
        ? realData.meals.map((m) => (m.id === meal.id ? meal : m))
        : [...realData.meals, meal],
    });
    setModal({ open: false, meal: null });
  };

  const deleteMeal = (id: string) => {
    if (readOnly) return;
    write({ meals: realData.meals.filter((m) => m.id !== id) });
  };

  const toggleFinished = () => {
    if (readOnly) return;
    const finishing = !realData.finished;
    // Finishing strips components (ingredients, scales, recipe components)
    // and chat transcripts from the day's meals (docs/adr/0001) — totals
    // stay; un-finishing restores nothing. `portion` is `scale`'s former
    // name and is dropped alongside it.
    write({
      finished: finishing,
      meals: finishing
        ? realData.meals.map(
            ({
              ingredients: _ingredients,
              scale: _scale,
              portion: _portion,
              recipeComponents: _recipeComponents,
              transcript: _transcript,
              ...rest
            }) => rest,
          )
        : realData.meals,
    });
  };

  return (
    <section
      className={`widget-card meal-log${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        <div className="widget-card-head-right">
          <span
            className="widget-card-score"
            data-testid={`widget-score-${config.id}`}
          >
            {hasConditions ? (
              <>
                <b>{score}</b>
                <span className="max"> / {maxDay}p</span>
              </>
            ) : (
              <span className="max">—</span>
            )}
          </span>
          {collapsible ? (
            <button
              type="button"
              className="widget-card-toggle"
              onClick={toggle}
              aria-label={collapsed ? "Utvid" : "Skjul"}
              aria-expanded={!collapsed}
              data-testid={`widget-collapse-${config.id}`}
            >
              <ChevronDown className="chev" size={16} />
            </button>
          ) : null}
        </div>
      </header>
      {collapsed ? (
        <div
          className="widget-card-summary"
          data-testid={`widget-summary-${config.id}`}
        >
          <span
            className="summary-line"
            data-testid={`meal-summary-totals-${config.id}`}
          >
            {MEAL_METRICS.map(
              (metric) =>
                `${formatAmount(totals[metric])} ${METRIC_LABELS_SHORT[metric]}`,
            ).join(" · ")}
          </span>
          {hasConditions ? (
            <span className="summary-line summary-conditions">
              {conditions.map((condition) => {
                const met = isConditionMet(condition, totals);
                return (
                  <span
                    key={condition.id}
                    className={`summary-condition ${met ? "met" : "unmet"}`}
                    data-met={met ? "true" : "false"}
                    data-testid={`meal-summary-condition-${condition.id}`}
                  >
                    {met ? (
                      <Check size={12} aria-hidden="true" />
                    ) : (
                      <X size={12} aria-hidden="true" />
                    )}
                    {conditionLabel(condition)}
                  </span>
                );
              })}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="widget-card-body">
        {meals.length === 0 ? (
          <p className="hint" data-testid={`meal-log-empty-${config.id}`}>
            Ingen måltider logget.
          </p>
        ) : (
          <div className="week-matrix meal-table">
            <table data-testid={`meal-table-${config.id}`}>
              <thead>
                <tr>
                  <th className="th-group">Måltid</th>
                  {MEAL_METRICS.map((metric) => (
                    <th key={metric}>{METRIC_LABELS_SHORT[metric]}</th>
                  ))}
                  {!readOnly ? <th /> : null}
                </tr>
              </thead>
              <tbody>
                {meals.map((meal) => (
                  <tr key={meal.id} data-testid={`meal-row-${meal.id}`}>
                    <td className="td-label">{meal.name}</td>
                    {MEAL_METRICS.map((metric) => (
                      <td key={metric}>
                        {formatAmount(meal[metric])}
                        <MetricCellDensity
                          conditions={conditions}
                          metric={metric}
                          amounts={meal}
                          testidFor={(conditionId) =>
                            `density-indicator-${meal.id}-${conditionId}`
                          }
                        />
                      </td>
                    ))}
                    {!readOnly ? (
                      <td className="td-actions">
                        <button
                          type="button"
                          className="icon-btn-edit"
                          onClick={() => setModal({ open: true, meal })}
                          aria-label="Rediger måltid"
                          data-testid={`edit-meal-button-${meal.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn-delete"
                          onClick={() => deleteMeal(meal.id)}
                          aria-label="Slett måltid"
                          data-testid={`delete-meal-button-${meal.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
                <tr
                  className="row-total"
                  data-testid={`meal-totals-${config.id}`}
                >
                  <td className="td-label">Totalt</td>
                  {MEAL_METRICS.map((metric) => (
                    <td
                      key={metric}
                      data-testid={`meal-total-${metric}-${config.id}`}
                    >
                      {formatAmount(totals[metric])}
                      <MetricCellDensity
                        conditions={conditions}
                        metric={metric}
                        amounts={totals}
                        testidFor={(conditionId) =>
                          `density-indicator-total-${conditionId}-${config.id}`
                        }
                      />
                    </td>
                  ))}
                  {!readOnly ? <td /> : null}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {hasConditions ? (
          <ul
            className="meal-conditions"
            data-testid={`meal-conditions-${config.id}`}
          >
            {conditions.map((condition) => {
              const met = isConditionMet(condition, totals);
              return (
                <li
                  key={condition.id}
                  className={met ? "met" : "unmet"}
                  data-testid={`condition-status-${condition.id}`}
                >
                  {conditionLabel(condition)} —{" "}
                  {formatAmount(totals[condition.metric])} {met ? "✓" : "✗"} ·{" "}
                  {condition.points}p
                </li>
              );
            })}
          </ul>
        ) : null}

        {!readOnly ? (
          <div className="meal-log-actions">
            <button
              type="button"
              className="btn ghost icon-btn"
              onClick={() => setModal({ open: true, meal: null })}
              data-testid={`add-meal-button-${config.id}`}
            >
              <Plus size={14} />
              Legg til måltid
            </button>
            <button
              type="button"
              className="btn ghost icon-btn"
              onClick={() => setClarificationsOpen(true)}
              data-testid={`food-clarifications-button-${config.id}`}
            >
              <NotebookPen size={14} />
              Matpresiseringer
            </button>
            <button
              type="button"
              className="btn ghost icon-btn"
              onClick={() => setRecipesOpen(true)}
              data-testid={`recipes-button-${config.id}`}
            >
              <ChefHat size={14} />
              Oppskrifter
            </button>
            <button
              type="button"
              className={realData.finished ? "btn ghost" : "btn-primary"}
              onClick={toggleFinished}
              data-testid={`finish-day-button-${config.id}`}
            >
              {realData.finished ? "Åpne dagen igjen" : "Ferdig for i dag"}
            </button>
          </div>
        ) : null}
      </div>
      <MealFormModal
        open={modal.open}
        initial={modal.meal}
        conditions={conditions}
        foodClarifications={foodClarifications.map((f) => f.text)}
        recipes={recipes}
        onSaveRecipe={saveRecipe}
        onSave={saveMeal}
        onClose={() => setModal({ open: false, meal: null })}
      />
      <FoodClarificationsModal
        open={clarificationsOpen}
        initial={foodClarifications}
        onSave={saveClarifications}
        onClose={() => setClarificationsOpen(false)}
      />
      <RecipesModal
        open={recipesOpen}
        initial={recipes}
        foodClarifications={foodClarifications.map((f) => f.text)}
        onSave={saveRecipes}
        onClose={() => setRecipesOpen(false)}
      />
    </section>
  );
}
